// frontend/client/src/utils/colorContrast.js
//
// Universal contrast-safe text color utility. Works for ANY background
// color — brand colors extracted from an Instagram scan, colors a user
// picks by hand, platform brand colors, anything — independent of
// whether the app is in light or dark mode.
//
// Dark mode (ThemeContext.js + the .dark block in index.css) controls the
// APP'S OWN surface colors. This utility controls what's readable ON TOP
// OF a specific color, which can be anything a user picks regardless of
// theme. They're separate concerns on purpose — this file doesn't know
// or care whether dark mode is on, and it shouldn't need to.

/**
 * Parses a hex color string (#RGB, #RRGGBB, or without #) into {r,g,b}.
 * Returns null if the string isn't a valid hex color.
 */
function parseHex(hex) {
  if (!hex || typeof hex !== 'string') return null;
  let h = hex.replace('#', '').trim();
  if (h.length === 3) h = h.split('').map(c => c + c).join('');
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
  return {
    r: parseInt(h.substring(0, 2), 16),
    g: parseInt(h.substring(2, 4), 16),
    b: parseInt(h.substring(4, 6), 16),
  };
}

/** WCAG relative luminance (0 = black, 1 = white). */
function relativeLuminance({ r, g, b }) {
  const srgb = [r, g, b].map(v => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * srgb[0] + 0.7152 * srgb[1] + 0.0722 * srgb[2];
}

/** WCAG contrast ratio between two relative luminances. */
function contrastRatio(lum1, lum2) {
  const lighter = Math.max(lum1, lum2);
  const darker = Math.min(lum1, lum2);
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Given ANY background color (hex), returns whichever of black or white
 * text has the higher WCAG contrast ratio against it. This is what makes
 * it theme-independent: it checks the actual color behind the text, not
 * whether dark mode happens to be on.
 *
 * Falls back to near-black if the input isn't parseable (missing color,
 * bad data from an extraction pipeline, etc.) so a bad value never throws
 * or silently renders invisible text.
 */
export function getContrastText(bgColor, { darkText = '#1F2937', lightText = '#FFFFFF' } = {}) {
  const rgb = parseHex(bgColor);
  if (!rgb) return darkText;

  const bgLum = relativeLuminance(rgb);
  const contrastWithBlack = contrastRatio(bgLum, 0);
  const contrastWithWhite = contrastRatio(bgLum, 1);

  return contrastWithBlack >= contrastWithWhite ? darkText : lightText;
}

/**
 * True if a background/text pair meets WCAG AA for normal text (4.5:1).
 * Useful if you'd rather flag a borderline color than just swap to
 * black/white — e.g. to add a safety text-shadow on near-mid-tone colors
 * where black/white are both weak.
 */
export function meetsContrastAA(bgColor, textColor) {
  const bg = parseHex(bgColor);
  const text = parseHex(textColor);
  if (!bg || !text) return false;
  return contrastRatio(relativeLuminance(bg), relativeLuminance(text)) >= 4.5;
}

/**
 * Convenience: ready-to-spread inline style object with contrast-safe
 * text already computed.
 *   <button style={getBrandButtonStyle(userData.brandColors?.[0])}>...</button>
 */
export function getBrandButtonStyle(bgColor, fallbackBg = '#4F46E5') {
  const background = bgColor || fallbackBg;
  return {
    background,
    color: getContrastText(background),
  };
}

/**
 * Sets --contrast-fg as an inline CSS custom property so the .contrast-text
 * utility already defined in index.css picks it up automatically, instead
 * of setting color directly. Useful when child elements (not just the
 * background element itself) need to inherit the computed color.
 *   const style = getContrastStyle(userData.brandColors?.[0]);
 *   <div style={style} className="contrast-text">...</div>
 */
export function getContrastStyle(bgColor, fallbackBg = '#4F46E5') {
  const background = bgColor || fallbackBg;
  return {
    background,
    '--contrast-fg': getContrastText(background),
  };
}